import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { PlanView } from './PlanViewSwitcher';

/**
 * The List-vs-Timeline choice, readable and writable from anywhere under the
 * plan panel.
 *
 * It exists for one caller: the date picker's "Set dates in the timeline" link
 * sits four levels down (`PlanTree → PlanRow → InlineDateCell`) and has to ask
 * for a view change, while the state itself belongs to `PlanPanel`. Threading a
 * callback through the virtualiser's row renderer to reach it would be four
 * props carrying one.
 *
 * Deliberately a context and NOT a slot in the editor reducer. View mode is a
 * *preference*, not document state — in the reducer, every inbound sync
 * (`RESET_FROM_SERVER` / `ADOPT_SERVER`) would snap the user back to the list.
 * That is why `PlanPanel` holds it in `usePersistedState`, and this only lifts
 * the same value into reach rather than moving it.
 */
export type PlanViewContextValue = {
  view: PlanView;
  setView: (next: PlanView) => void;
};

const PlanViewContext = createContext<PlanViewContextValue | null>(null);

export type PlanViewProviderProps = PlanViewContextValue & { children: ReactNode };

export const PlanViewProvider = ({ view, setView, children }: PlanViewProviderProps) => {
  const value = useMemo(() => ({ view, setView }), [view, setView]);

  return <PlanViewContext.Provider value={value}>{children}</PlanViewContext.Provider>;
};

export const usePlanView = (): PlanViewContextValue => {
  const value = useContext(PlanViewContext);
  if (value === null) {
    throw new Error('usePlanView must be called inside a PlanViewProvider');
  }
  return value;
};
